import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
    AttachmentThumbnail,
    DropdownMenu,
    InputTextArea,
    Label,
    Modal,
    SentimentRating,
    SparkModalPortal,
    Spinner,
    sparkToast,
} from '@spark';
import { useAuth } from '@/features/auth/AuthContext';
import { createFeedbackTask, uploadFeedbackAttachment } from '../api/feedbackApi';
import {
    FEEDBACK_ALLOWED_IMAGE_TYPES,
    FEEDBACK_ATTACHMENT_LIMIT_ERROR,
    FEEDBACK_ATTACHMENT_TYPE_ERROR,
    FEEDBACK_CATEGORIES,
    FEEDBACK_DETAILS_MAX_LENGTH,
    FEEDBACK_MAX_ATTACHMENT_BYTES,
    FEEDBACK_MAX_ATTACHMENTS,
    FEEDBACK_MODAL_WIDTH,
    FEEDBACK_SENTIMENTS,
} from '../constants';
import '../feedback.css';

export const FEEDBACK_SENT_MESSAGE = 'Thanks! Your feedback has been sent.';
export const FEEDBACK_SENTIMENT_LABEL = 'How is your experience with Specific so far?';
export const FEEDBACK_SENDING_LABEL = 'Sending feedback';

/**
 * Upload every screenshot at once (the backend verifies the task per upload,
 * so the requests are independent) and return how many of them failed.
 */
export async function uploadAttachmentsInParallel(taskId, attachments) {
    const results = await Promise.allSettled(
        attachments.map((attachment) => uploadFeedbackAttachment(taskId, attachment.file)),
    );
    const failed = results.filter((result) => result.status === 'rejected');
    failed.forEach((result) => console.error('Feedback attachment upload failed:', result.reason));
    return failed.length;
}

const CATEGORY_ITEMS = FEEDBACK_CATEGORIES.map(({ value, iconSlug }) => ({
    label: value,
    showPrefixIcon: true,
    prefixIconSlug: iconSlug,
}));

/**
 * Validate a dropped file list against the current attachments. Returns the
 * helper copy to show, or ``null`` when every file may be added. A drop is
 * all-or-nothing so the user never has to guess which files made it in.
 */
export function validateDroppedFiles(files, currentCount) {
    if (!files.length) return null;
    if (files.some((file) => !FEEDBACK_ALLOWED_IMAGE_TYPES.includes(file.type))) {
        return FEEDBACK_ATTACHMENT_TYPE_ERROR;
    }
    if (
        currentCount + files.length > FEEDBACK_MAX_ATTACHMENTS ||
        files.some((file) => file.size > FEEDBACK_MAX_ATTACHMENT_BYTES)
    ) {
        return FEEDBACK_ATTACHMENT_LIMIT_ERROR;
    }
    return null;
}

const revokePreviews = (attachments) => {
    attachments.forEach((attachment) => URL.revokeObjectURL(attachment.previewUrl));
};

/**
 * "Share your thoughts" modal (Figma 9261-8293 / 9261-8847): optional
 * sentiment, required category + details, and up to five dropped screenshots
 * that are uploaded as ClickUp task attachments after the task is created.
 */
export default function FeedbackModal({ open, onClose }) {
    const { workspace } = useAuth();
    const [sentiment, setSentiment] = useState(null);
    const [category, setCategory] = useState(null);
    const [details, setDetails] = useState('');
    const [attachments, setAttachments] = useState([]);
    const [attachmentError, setAttachmentError] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    // Latest attachments for the unmount cleanup, which cannot see fresh state.
    const attachmentsRef = useRef(attachments);
    attachmentsRef.current = attachments;

    useEffect(() => {
        if (!open) return undefined;
        setSentiment(null);
        setCategory(null);
        setDetails('');
        setAttachments([]);
        setAttachmentError(null);
        setSubmitting(false);
        return () => {
            revokePreviews(attachmentsRef.current);
        };
    }, [open]);

    const trimmedDetails = details.trim();
    const canSubmit = Boolean(category) && trimmedDetails.length > 0 && !submitting;

    const close = () => (submitting ? null : onClose?.());

    const handleDragOver = (event) => {
        // Required so the browser lets the drop event fire (and does not open the file).
        event.preventDefault();
        event.stopPropagation();
    };

    const handleDrop = (event) => {
        event.preventDefault();
        event.stopPropagation();
        if (submitting) return;
        const files = Array.from(event.dataTransfer?.files ?? []);
        const error = validateDroppedFiles(files, attachments.length);
        if (error) {
            setAttachmentError(error);
            return;
        }
        if (!files.length) return;
        setAttachmentError(null);
        setAttachments((prev) => [
            ...prev,
            ...files.map((file) => ({
                id: `${file.name}-${file.size}-${file.lastModified}-${Math.random().toString(36).slice(2)}`,
                file,
                previewUrl: URL.createObjectURL(file),
            })),
        ]);
    };

    const removeAttachment = useCallback((id) => {
        setAttachments((prev) => {
            const removed = prev.find((attachment) => attachment.id === id);
            if (removed) URL.revokeObjectURL(removed.previewUrl);
            return prev.filter((attachment) => attachment.id !== id);
        });
        setAttachmentError(null);
    }, []);

    const submit = async () => {
        if (!canSubmit) return;
        setSubmitting(true);
        try {
            const task = await createFeedbackTask({
                category,
                sentiment: sentiment ? FEEDBACK_SENTIMENTS[sentiment] : null,
                details: trimmedDetails,
                workspace: workspace?.name ?? null,
            });
            const failedUploads = await uploadAttachmentsInParallel(task.task_id, attachments);
            if (failedUploads > 0) {
                sparkToast.warning(`Feedback sent, but ${failedUploads} attachment(s) failed to upload.`);
            } else {
                sparkToast.success(FEEDBACK_SENT_MESSAGE);
            }
            setSubmitting(false);
            onClose?.();
        } catch (err) {
            sparkToast.error(err?.detail || 'Could not send your feedback. Please try again.');
            setSubmitting(false);
        }
    };

    const thumbnails = attachments.length
        ? attachments.map((attachment) => (
              <AttachmentThumbnail
                  key={attachment.id}
                  src={attachment.previewUrl}
                  name={attachment.file.name}
                  disabled={submitting}
                  onRemove={() => removeAttachment(attachment.id)}
              />
          ))
        : undefined;

    return (
        <SparkModalPortal open={open} onClose={close} width={FEEDBACK_MODAL_WIDTH} destroyOnClose>
            <Modal
                open
                className="feedback-modal"
                title="Share your thoughts"
                showSubtitle={false}
                showOverlay={false}
                showScrollBar={false}
                fixedHeight={false}
                primaryLabel="Submit Feedback"
                primaryIcon={submitting ? <Spinner size="small" aria-label={FEEDBACK_SENDING_LABEL} /> : undefined}
                primaryDisabled={!canSubmit}
                tertiaryLabel="Cancel"
                tertiaryDisabled={submitting}
                onPrimaryClick={submit}
                onTertiaryClick={close}
                onCloseClick={close}
            >
                <form
                    className="feedback-modal__form"
                    aria-busy={submitting || undefined}
                    onSubmit={(event) => {
                        event.preventDefault();
                        submit();
                    }}
                >
                    <div className="feedback-modal__sentiment">
                        <Label label={FEEDBACK_SENTIMENT_LABEL} />
                        <SentimentRating
                            value={sentiment}
                            onChange={setSentiment}
                            disabled={submitting}
                            aria-label={FEEDBACK_SENTIMENT_LABEL}
                        />
                    </div>
                    <DropdownMenu
                        className="feedback-modal__field"
                        size="medium"
                        fill
                        label="Select Category"
                        showAsterisk
                        placeholder="Select"
                        items={CATEGORY_ITEMS}
                        value={category ?? undefined}
                        text={category ?? undefined}
                        disabled={submitting}
                        onSelect={(item) => setCategory(item.label)}
                    />
                    <div className="feedback-modal__dropzone" onDragOver={handleDragOver} onDrop={handleDrop}>
                        <InputTextArea
                            className="feedback-modal__field"
                            label="Tell us more"
                            showAsterisk
                            placeholder="Please share more details, You can also attach a screenshot"
                            value={details}
                            onChange={(event) => setDetails(event.target.value)}
                            maxLength={FEEDBACK_DETAILS_MAX_LENGTH}
                            disabled={submitting}
                            danger={Boolean(attachmentError)}
                            showHelperText={Boolean(attachmentError)}
                            helperText={attachmentError ?? ''}
                            attachments={thumbnails}
                        />
                    </div>
                </form>
            </Modal>
        </SparkModalPortal>
    );
}
